/**
 * @praesto/core/events
 *
 * In-memory, synchronous, type-safe event bus. The ONLY sanctioned channel
 * for cross-module communication (modules never import each other).
 *
 * Contract: packages/contracts/src/core-api.ts (re-exported via events.ts).
 */
import type { EventBus, EventName, EventPayloads, Unsubscribe } from "@praesto/contracts";

type ErasedHandler = (payload: unknown) => void;

export function createEventBus(): EventBus {
  const handlers = new Map<EventName, Set<ErasedHandler>>();

  return {
    emit<T extends EventName>(event: T, payload: EventPayloads[T]): void {
      const set = handlers.get(event);
      if (!set) return;
      // Iterate a snapshot so a handler that (un)subscribes during dispatch
      // does not corrupt the iteration. A throwing handler is isolated so it
      // neither breaks sibling handlers nor escapes to the emitter.
      for (const handler of Array.from(set)) {
        try {
          handler(payload);
        } catch (err) {
          console.error(`[events] handler for "${event}" threw:`, err);
        }
      }
    },

    on<T extends EventName>(event: T, handler: (payload: EventPayloads[T]) => void): Unsubscribe {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      const erased = handler as ErasedHandler;
      set.add(erased);

      let active = true;
      return () => {
        if (!active) return;
        active = false;
        const current = handlers.get(event);
        current?.delete(erased);
        if (current && current.size === 0) handlers.delete(event);
      };
    },
  };
}
